<template>
  <div id="app">
    <router-view
      v-if="showRouterView"
      v-show="hideScreenOverlay">
    </router-view>
    <transition name="slide-up">
      <ScreenOverlay
        v-show="!hideScreenOverlay" />
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import ScreenOverlay from '@/views/ScreenOverlay.vue';

export default {
  name: 'App',
  components: {
    ScreenOverlay,
  },
  data() {
    return {
      hideScreenOverlay: false,
      focusEventFromAddingViaExtension: false,
      lastPolled: null,
    };
  },
  computed: {
    ...mapState({
      initialDataLoaded: state => state.appState.initialDataLoaded,
      appIsReady: state => state.appState.appIsReady,
      loggingOut: state => state.appState.screenOverlayLogout,
    }),
    ...mapGetters({
      userIsLoggedIn: 'users/isAuthenticated',
      syncStatus: 'sync/syncStatus',
    }),
    showRouterView() {
      return (this.appIsReady && !this.loggingOut && this.$route.name !== 'NoInternet') || this.$route.name === 'Login';
    },
  },
  methods: {
    ...mapActions({
      persistAll: 'sync/persistAll',
      getDataForPolling: 'users/getDataForPolling',
    }),
    syncBeforeUnload(e) {
      // Sync if necessary
      if (this.syncStatus.status !== 'Synced') {
        this.persistAll({ beforeLeaving: true });
        e.preventDefault();
        e.returnValue = ''; // eslint-disable-line
      }
    },
    handleMessage(event) {
      // Only accept messages from the same frame
      if (event.source !== window) {
        return;
      }

      const { source, payload } = event.data;
      if (!payload) return;
      const { type, url, title } = payload;
      // Check whether from extension and the correct type
      if (source === 'byblio-contentscript' && type === 'add-card-with-url') {
        // Using a date object for extra check. E.g. in case you add via extension without focussing the window, the pollServer even't
        // won't execute and therefore not reset this indicator. Now the pollServer event will take into account the time passed to check
        // whether the focus event is from the extension or not
        this.focusEventFromAddingViaExtension = new Date();

        this.$navigateTo({ path: '/card/0', query: { url, title } });
      }
    },
    checkPollingShortcut(e) {
      if (e.altKey && e.key === 'p') this.pollServer({ force: true });
    },
    pollServer({ force, delay = 50 }) {
      // secondsAgo is used to prevent too much polling the db as a result of switching tabs
      const secondsAgo = differenceInSeconds(new Date(), this.lastPolled);
      if (!force && (secondsAgo < 5 || !this.initialDataLoaded)) return;

      // A small timeout to enable us to check the focusEvent condition
      // >> Though actually I do not think we need this. It seems handleMessage is anyway called before this one.
      setTimeout(() => {
        if (this.focusEventFromAddingViaExtension && differenceInSeconds(new Date(), this.focusEventFromAddingViaExtension) < 2) {
          // In this case we want to wait for some time until adding the card via the extension is done. 500ms should be fine.
          this.focusEventFromAddingViaExtension = false;
          this.pollServer({ delay: 500 });
          return;
        }

        this.lastPolled = new Date();
        this.getDataForPolling();
      }, delay);
    },
  },
  created() {
    window.addEventListener('focus', this.pollServer);
    window.addEventListener('keydown', this.checkPollingShortcut);
  },
  destroyed() {
    window.removeEventListener('focus', this.pollServer);
    window.removeEventListener('keydown', this.checkPollingShortcut);
  },
  mounted() {
    if (this.showRouterView) this.hideScreenOverlay = true;

    // Check for IE, display alert (other browsers are quite fine, but IE is stupid and does not work properly)
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    const trident = ua.indexOf('Trident/');
    if (msie > 0 || trident > 0) window.alert('Your browser, Internet Explorer, does not handle Byblio well. Please use a different browser (such as Chrome or Firefox).'); // eslint-disable-line

    // Sync if necessary
    window.addEventListener('beforeunload', this.syncBeforeUnload);

    // Listen for messages from extension (via contentscript)
    window.addEventListener('message', this.handleMessage);
  },
  watch: {
    showRouterView() {
      if (this.showRouterView) {
        setTimeout(() => {
          this.hideScreenOverlay = true;
        }, 150);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.syncBeforeUnload);
    window.removeEventListener('message', this.handleMessage);
  },
};
</script>

<style lang="scss">
@import '~styles/colors';
@import '~styles/fonts';

html {
  overflow: hidden !important;
}
body {
  font-family: 'Open Sans', sans-serif !important;
  /*-ms-overflow-style: none; /* for Edge */
  /*overflow: -moz-scrollbars-none; /* for FireFox < v68 */
  overflow: hidden;
}
::-webkit-scrollbar {
  height: 6px;
  width: 6px;
  box-shadow: 0 0 2px 1px rgba(0, 0, 0, 0.1) inset;
  padding: 0 2px;
}
::-webkit-scrollbar-thumb {
  height: 6px;
  width: 6px;
  border-radius: 10px;
  background-color: $primary-dark;
  box-shadow: -2px -2px 2px 1px rgba(0, 0, 0, 0.3) outset;
}
</style>

<style lang="scss">
/* setting buefy variables */
@import  '~bulma/sass/utilities/_all';
@import '~styles/colors';
@import '~styles/fonts';

// Setup $colors to use as bulma classes (e.g. 'is-twitter')
$colors: (
    "white": ($white, $black),
    "black": ($black, $white),
    "lightgrey": ($lightgrey, $darkgrey),
    "darkgrey": ($darkgrey, $lightgrey),
    "darkblue": ($darkblue, $lightgrey),
    "blue": ($blue, $lightgrey),
    "green": ($green, $lightgrey),
    "warning": ($red, $lightgrey),
    "danger": ($brightred, $lightgrey),
    "primary": ($primary, $black),
    "primary-dark": ($primary-dark, $black),
    "primary-light": ($primary-light, $black),
    "secondary": ($secondary, $black),
    "secondary-dark": ($secondary-dark, $primary-light),
    "secondary-light": ($secondary-light, $black),
);

@import '~bulma';
@import '~buefy/src/scss/buefy';

.byblio {
  font-family: $quattrocento;
  cursor: default;
  user-select: none;
}
.cursor-pointer {
  cursor: pointer;
}
</style>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
  z-index: 0;
}
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.4s;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(-100vh);
}
</style>
